import React, { useCallback, useEffect, useState } from 'react';
import { Mail, Plus, X, Loader2, BellRing } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { adminListRecipients, adminAddRecipient, adminRemoveRecipient } from '../../api';
import { useToast } from '../../hooks/use-toast';

const NotificationRecipientsCard = () => {
  const { toast } = useToast();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try { const data = await adminListRecipients(); setEmails(data.emails || []); }
    catch (err) { console.warn('load recipients failed', err?.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const v = newEmail.trim();
    if (!v) return;
    setAdding(true);
    try {
      const data = await adminAddRecipient(v);
      setEmails(data.emails);
      setNewEmail('');
      toast({ title: 'Recipient added', description: v });
    } catch (err) {
      toast({ title: 'Could not add', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setAdding(false); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from notifications?`)) return;
    try {
      const data = await adminRemoveRecipient(email);
      setEmails(data.emails);
      toast({ title: 'Recipient removed' });
    } catch (err) {
      toast({ title: 'Could not remove', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  return (
    <div className="rounded-xl border border-cyan-400/10 bg-slate-900/30 p-7">
      <div className="flex items-center gap-3 mb-2">
        <BellRing className="w-5 h-5 text-cyan-400" />
        <h3 className="text-xl font-semibold text-white">Notification Recipients</h3>
      </div>
      <p className="text-sm text-slate-400 mb-5">Email addresses that will be notified when a new lead or job application is submitted. Resumes are attached automatically.</p>

      {loading ? (
        <div className="text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading...</div>
      ) : (
        <div className="space-y-2 mb-5">
          {emails.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No recipients configured &mdash; notifications are disabled.</p>
          ) : emails.map(e => (
            <div key={e} className="flex items-center justify-between gap-3 rounded-md border border-cyan-400/10 bg-[#080d18] px-4 py-2.5">
              <span className="text-sm text-slate-200 inline-flex items-center gap-2"><Mail className="w-4 h-4 text-cyan-400" />{e}</span>
              <button onClick={() => remove(e)} className="text-slate-400 hover:text-red-400 transition" title="Remove">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={add} className="flex items-end gap-2">
        <div className="flex-1">
          <Label className="text-slate-300 text-xs mb-1.5 block">Add Recipient</Label>
          <Input type="email" required value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="name@company.com" className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" />
        </div>
        <Button type="submit" disabled={adding} className="bg-cyan-400 hover:bg-cyan-300 disabled:opacity-70 text-slate-900 font-semibold rounded-md h-10 px-4">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
        </Button>
      </form>
    </div>
  );
};

export default NotificationRecipientsCard;
